/**
 * What an image file says about itself before anything decodes it (decision 56: dimensions checked before decoding;
 * part 16a log, table H): its format, from the signature, and the width and height the decoder will use, from the header
 * the decoder reads them from. Nothing here decodes pixels, and each walk is bounded by the bytes it is given.
 */

/** A format an image may arrive in. Only JPEG and PNG are stored (decision 37); WebP is read and re-encoded. */
export type InputFormat = 'jpeg' | 'png' | 'webp';

/** A stored image's format (decision 37: JPEG for photos, PNG for line art and transparency; no WebP). */
export type ImageFormat = 'jpeg' | 'png';

/** An image's format and size in pixels, as its header gives them. */
export interface ImageHeader {
  readonly format: InputFormat;
  readonly width: number;
  readonly height: number;
  /**
   * Whether the file can hold transparency (table H, column α): false only when its format or header rules it out (a
   * JPEG; a PNG of grey, colour or palette with no tRNS before its data; a lossy WebP; an extended WebP without its alpha
   * flag); in doubt, true.
   */
  readonly alpha: boolean;
}

/** Why an input isn't read: no signature this app takes (H1), or a header that doesn't hold (H3, H5, H7). */
export type HeaderProblem = 'unsupported' | 'damaged';

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const RIFF = [0x52, 0x49, 0x46, 0x46];
const WEBP = [0x57, 0x45, 0x42, 0x50];

/** JPEG markers this module names. */
export const SOI = 0xd8;
export const EOI = 0xd9;
export const SOS = 0xda;

/** Whether `bytes` holds `prefix` at `at`. */
export function holds(bytes: Uint8Array, prefix: readonly number[], at = 0): boolean {
  return prefix.every((byte, index) => bytes[at + index] === byte);
}

/** The four bytes at `at` as text (a PNG chunk's or a RIFF chunk's type); shorter at the end. */
export function fourCc(bytes: Uint8Array, at: number): string {
  return String.fromCharCode(...bytes.subarray(at, at + 4));
}

/** A view of `bytes` to read whole numbers from. */
function view(bytes: Uint8Array): DataView {
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
}

/** The byte at `at`, which the caller has checked is there. */
export function byteAt(bytes: Uint8Array, at: number): number {
  return view(bytes).getUint8(at);
}

/** The big-endian 32-bit whole number at `at`, or null past the end. */
export function uint32(bytes: Uint8Array, at: number): number | null {
  return at + 4 > bytes.length ? null : view(bytes).getUint32(at);
}

/** The little-endian whole number of `length` bytes (2 to 4) at `at`, which the caller has checked are there. */
function littleEndian(bytes: Uint8Array, at: number, length: 2 | 3 | 4): number {
  const fields = view(bytes);
  if (length === 4) return fields.getUint32(at, true);
  const low = fields.getUint16(at, true);
  return length === 2 ? low : low + fields.getUint8(at + 2) * 0x10000;
}

/**
 * One JPEG marker segment: its marker, where its first 0xFF is, where its body starts (after the length) and where it
 * ends. A standalone marker (SOI, EOI, RSTn, TEM) has an empty body.
 */
export interface Segment {
  readonly marker: number;
  readonly start: number;
  readonly body: number;
  readonly end: number;
}

/** Whether `marker` stands alone, with no length after it: TEM, RST0–RST7, SOI, EOI. */
function standalone(marker: number): boolean {
  return marker === 0x01 || (marker >= 0xd0 && marker <= SOI) || marker === EOI;
}

/**
 * The JPEG segment whose marker starts at `at` (fill bytes 0xFF allowed before it), or null when there is no marker
 * there or its length runs short of 2 or past the end (H3).
 */
export function segmentAt(bytes: Uint8Array, at: number): Segment | null {
  if (bytes[at] !== 0xff) return null;
  let index = at + 1;
  while (bytes[index] === 0xff) index++;
  const body = index + 1;
  // No marker past the end; 0x00 after 0xFF is a stuffed byte, no marker.
  if (body > bytes.length || byteAt(bytes, index) === 0x00) return null;
  const marker = byteAt(bytes, index);
  if (standalone(marker)) return { marker, start: at, body, end: body };
  if (body + 2 > bytes.length) return null;
  // A length under 2 ends the segment inside its own length field, whose first byte (0x00) no read takes for a marker.
  const end = body + view(bytes).getUint16(body);
  return end > bytes.length ? null : { marker, start: at, body: body + 2, end };
}

/** Whether `marker` starts a frame (SOF0–SOF15), which DHT (C4), JPG (C8) and DAC (CC) share the range with. */
export function isFrame(marker: number): boolean {
  return marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc;
}

/** H2, H3: a JPEG's size from its first frame header, the segments before it walked by their lengths. */
function jpegHeader(bytes: Uint8Array): ImageHeader | HeaderProblem {
  let at = 2;
  for (;;) {
    const segment = segmentAt(bytes, at);
    if (segment === null || segment.marker === SOS || segment.marker === EOI) return 'damaged';
    if (isFrame(segment.marker)) {
      // The frame header: precision, height, width, component count (B.2.2).
      if (segment.end - segment.body < 6) return 'damaged';
      const fields = view(bytes);
      const height = fields.getUint16(segment.body + 1);
      const width = fields.getUint16(segment.body + 3);
      return width === 0 || height === 0 ? 'damaged' : { format: 'jpeg', width, height, alpha: false };
    }
    at = segment.end;
  }
}

/** The largest PNG dimension (2³¹ − 1). */
const PNG_MAX = 0x7fffffff;

/**
 * H4α: whether a PNG can hold transparency: its colour type says so (grey or colour with alpha, or any type this module
 * doesn't know), or a tRNS chunk comes before its first data chunk; a walk that runs out of chunks can't tell, so says yes.
 */
function pngAlpha(bytes: Uint8Array): boolean {
  const type = bytes[25];
  if (type !== 0 && type !== 2 && type !== 3) return true;
  for (let at = 8; ; ) {
    const length = uint32(bytes, at);
    if (length === null) return true;
    const kind = fourCc(bytes, at + 4);
    if (kind === 'tRNS') return true;
    if (kind === 'IDAT' || kind === 'IEND') return false;
    at += 12 + length;
  }
}

/** H4, H5: a PNG's size from its IHDR, which must be the first chunk. */
function pngHeader(bytes: Uint8Array): ImageHeader | HeaderProblem {
  // The signature, IHDR's length and type, its width and height: 24 bytes.
  if (bytes.length < 24) return 'damaged';
  const fields = view(bytes);
  const width = fields.getUint32(16);
  const height = fields.getUint32(20);
  if (fields.getUint32(8) !== 13 || fourCc(bytes, 12) !== 'IHDR') return 'damaged';
  if (width === 0 || height === 0 || width > PNG_MAX || height > PNG_MAX) return 'damaged';
  return { format: 'png', width, height, alpha: pngAlpha(bytes) };
}

/** Where a WebP's first chunk's data starts: after `RIFF`, the size, `WEBP`, the chunk's type and its size. */
const WEBP_DATA = 20;

/** H6, H7: a WebP's size from its first chunk: a lossy frame, a lossless one, or the extended format's canvas. */
function webpHeader(bytes: Uint8Array): ImageHeader | HeaderProblem {
  const kind = fourCc(bytes, 12);
  const data = WEBP_DATA;
  if (kind === 'VP8 ') {
    // The key frame's start code, then 14-bit sizes, each with a 2-bit scale above it.
    if (bytes.length < data + 10 || !holds(bytes, [0x9d, 0x01, 0x2a], data + 3)) return 'damaged';
    const width = littleEndian(bytes, data + 6, 2) & 0x3fff;
    const height = littleEndian(bytes, data + 8, 2) & 0x3fff;
    // H6α: a lossy WebP alone has no alpha.
    return width === 0 || height === 0 ? 'damaged' : { format: 'webp', width, height, alpha: false };
  }
  if (kind === 'VP8L') {
    // The signature, then the width and height less one, 14 bits each, from the lowest bit up.
    if (bytes.length < data + 5 || bytes[data] !== 0x2f) return 'damaged';
    const bits = littleEndian(bytes, data + 1, 4);
    // H6α: the lossless format's alpha bit is a hint only, so it can always hold transparency.
    return { format: 'webp', width: (bits & 0x3fff) + 1, height: ((bits >>> 14) & 0x3fff) + 1, alpha: true };
  }
  if (kind === 'VP8X') {
    // Flags and reserved bytes, then the canvas's width and height less one, 24 bits each.
    if (bytes.length < data + 10) return 'damaged';
    // H6α: the alpha flag is set when any frame holds transparency.
    return { format: 'webp', width: littleEndian(bytes, data + 4, 3) + 1, height: littleEndian(bytes, data + 7, 3) + 1, alpha: (byteAt(bytes, data) & 0x10) !== 0 };
  }
  return 'damaged';
}

/**
 * H1: the format `bytes` say they are by their signature alone (a JPEG's `FF D8 FF`, PNG's eight bytes, RIFF…WEBP), or
 * null; nothing past the signature is read, so a damaged file of a format still names it (part 16c, P11).
 */
export function signatureOf(bytes: Uint8Array): InputFormat | null {
  if (holds(bytes, [0xff, SOI, 0xff])) return 'jpeg';
  if (holds(bytes, PNG_SIGNATURE)) return 'png';
  if (holds(bytes, RIFF) && holds(bytes, WEBP, 8)) return 'webp';
  return null;
}

/**
 * Table H: the format and size an image file's header gives, read without decoding; `unsupported` for anything but a
 * JPEG, PNG or WebP signature, `damaged` for a header that doesn't hold.
 */
export function readImageHeader(bytes: Uint8Array): ImageHeader | HeaderProblem {
  switch (signatureOf(bytes)) {
    case 'jpeg':
      return jpegHeader(bytes);
    case 'png':
      return pngHeader(bytes);
    case 'webp':
      return webpHeader(bytes);
    case null:
      return 'unsupported';
  }
}
